import React, { useEffect, useState } from "react";
import { load } from "@cashfreepayments/cashfree-js";

type CashfreeMode = "sandbox" | "production";
type RedirectTarget = "_self" | "_blank" | "_top";

const CARD_STYLE: React.CSSProperties = {
  width: "min(760px, 100%)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  background: "rgba(17, 24, 39, 0.72)",
  backdropFilter: "blur(6px)",
  borderRadius: 16,
  padding: 20,
  color: "#f7f7f8",
};

const CONTAINER_STYLE: React.CSSProperties = {
  margin: 0,
  minHeight: "100vh",
  fontFamily: "Inter, Arial, sans-serif",
  color: "#f7f7f8",
  background: "linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #111827 100%)",
  display: "grid",
  placeItems: "center",
  padding: 24,
};

const fieldStyle: React.CSSProperties = {
  width: "100%",
  borderRadius: 10,
  border: "1px solid rgba(255, 255, 255, 0.15)",
  background: "#0b1220",
  color: "#f9fafb",
  padding: "10px 12px",
  fontSize: "0.95rem",
};

const createOrderEndpoint =
  process.env.REACT_APP_CASHFREE_CREATE_ORDER_URL || "/api/create-order";

const CashfreeRedirectCheckout = () => {
  const [mode, setMode] = useState<CashfreeMode>("sandbox");
  const [redirectTarget, setRedirectTarget] = useState<RedirectTarget>("_top");
  const [sessionId, setSessionId] = useState("");
  const [status, setStatus] = useState("Loading Cashfree SDK...");
  const [loading, setLoading] = useState(false);
  const [cashfree, setCashfree] = useState<any>(null);

  useEffect(() => {
    initializeSDK(
      process.env.REACT_APP_CASHFREE_MODE === "production"
        ? "production"
        : "sandbox",
    );
  }, []);

  const initializeSDK = async (selected: CashfreeMode) => {
    setMode(selected as CashfreeMode);
    try {
      const cashfreeInstance = await load({
        mode: selected,
      });
      console.log("cashfreeInstance", cashfreeInstance);
      if (!cashfreeInstance) {
        setStatus("Cashfree SDK not loaded. Check network and script URL.");
        return;
      }
      setCashfree(cashfreeInstance);
      setStatus(`Cashfree initialized in ${selected} mode.`);
      initialCall(cashfreeInstance);
    } catch (error: any) {
      setStatus(
        `Failed to initialize Cashfree SDK: ${error?.message || "Unknown error"}`,
      );
    }
  };

  const initialCall = async (cashfreeInstance: any) => {
    const params = new URLSearchParams(window.location.search);
    const paymentSessionFromUrl = params.get("paymentsessionid");

    if (paymentSessionFromUrl) {
      console.log("paymentSessionFromUrl", paymentSessionFromUrl);
      setSessionId(paymentSessionFromUrl);
      setStatus("Payment session loaded from URL params.");
      setTimeout(() => {
        startPayment(paymentSessionFromUrl, cashfreeInstance);
      }, 100);
    }
  };

  const startPayment = async (
    paymentSessionId: string = sessionId,
    cashfreeInstance?: any,
  ) => {
    try {
      const cashfreeToUse = cashfreeInstance || cashfree;
      if (!cashfreeToUse) {
        setStatus("Cashfree is not ready yet. Please try again.");
        return;
      }

      setLoading(true);
      setStatus("Preparing checkout...");

      setStatus("Redirecting to Cashfree checkout...");

      cashfreeToUse.checkout({
        paymentSessionId,
        redirectTarget,
      });
    } catch (error: any) {
      setStatus(`Checkout error: ${error?.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={CONTAINER_STYLE}>
      <main style={CARD_STYLE}>
        <h1 style={{ margin: "0 0 8px", fontSize: "1.4rem" }}>
          Cashfree Redirect Checkout
        </h1>
        <p style={{ margin: "0 0 12px", color: "#d1d5db" }}>
          This page implements Cashfree Web Redirect flow. It needs a backend
          order creation API to securely generate{" "}
          <code>payment_session_id</code>.
        </p>

        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <div>
              <label
                style={{
                  fontSize: "0.9rem",
                  color: "#cbd5e1",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Environment
              </label>
              <select
                value={mode}
                onChange={(e) => {
                  initializeSDK(e.target.value as CashfreeMode);
                }}
                style={fieldStyle}
              >
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
            </div>

            <div>
              <label
                style={{
                  fontSize: "0.9rem",
                  color: "#cbd5e1",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Redirect target
              </label>
              <select
                value={redirectTarget}
                onChange={(e) =>
                  setRedirectTarget(e.target.value as RedirectTarget)
                }
                style={fieldStyle}
              >
                <option value="_self">_self (same tab)</option>
                <option value="_blank">_blank (new tab)</option>
                <option value="_top">_top (full window)</option>
              </select>
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: "0.9rem",
                color: "#cbd5e1",
                display: "block",
                marginBottom: 6,
              }}
            >
              Payment Session ID (optional)
            </label>
            <input
              type="text"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="session_xxxxx (if you already have one)"
              style={fieldStyle}
            />
            <p style={{ color: "#94a3b8", fontSize: "0.85rem", marginTop: 8 }}>
              Leave empty to fetch from backend endpoint{" "}
              <code>{createOrderEndpoint}</code>.
            </p>
          </div>

          <button
            type="button"
            onClick={() => startPayment(sessionId)}
            disabled={loading}
            style={{
              ...fieldStyle,
              cursor: loading ? "not-allowed" : "pointer",
              background: loading ? "#3b82f6aa" : "#2563eb",
              border: "none",
              fontWeight: 600,
            }}
          >
            {loading ? "Processing..." : "Pay Now"}
          </button>
        </div>

        <div
          style={{
            marginTop: 12,
            borderRadius: 10,
            border: "1px solid rgba(255, 255, 255, 0.12)",
            background: "rgba(15, 23, 42, 0.6)",
            padding: 10,
            fontSize: "0.9rem",
          }}
        >
          {status}
        </div>
      </main>
    </div>
  );
};

export default CashfreeRedirectCheckout;
